import { API_BASE, APP_VERSION_HEADER, RULES_VERSION_HEADER } from '../meta/online/schema';
import { RULES_VERSION } from '../meta/rulesVersion';
import { isNative } from './native';

/**
 * The network side of online play (docs/ONLINE.md, "The client"): the only place the game calls
 * the API. Everything here may fail quietly; the game never waits on it.
 */

/**
 * Online play is on in dev builds (against `pnpm server:dev`) and, in other builds, only once
 * `VITE_ONLINE=true` (.env.production): player names, the privacy policy and the store privacy
 * answers come first.
 */
export const ONLINE_ENABLED: boolean =
  import.meta.env.DEV || import.meta.env.VITE_ONLINE === 'true';

/** The app version, for the server's "update the game" answer. */
export const APP_VERSION: string = __APP_RELEASE__.replace(/^lawnmower@/, '').split('+')[0] ?? '';

/** The web game calls its own origin (the Vite proxy in development); the apps call the site. */
function apiBase(): string {
  return isNative() ? `https://playlawnmower.com${API_BASE}` : API_BASE;
}

/** An API path's address: absolute in the apps, relative to the page on the web. */
export function apiUrl(path: string): string {
  return `${apiBase()}${path}`;
}

/** False when the device knows it has no connection (true when it cannot tell). */
export function probablyOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

export type ApiAnswer = { status: number; body: unknown } | { status: 'network' };

/** Gives up on a request after this long: a slow network must not hold runs up forever. */
const TIMEOUT_MS = 10_000;

/** One API call. Never throws: a failure is `{ status: 'network' }`. */
export async function apiRequest(
  path: string,
  options: { method?: string; body?: unknown; token?: string } = {},
): Promise<ApiAnswer> {
  const headers: Record<string, string> = {
    [APP_VERSION_HEADER]: APP_VERSION,
    [RULES_VERSION_HEADER]: String(RULES_VERSION),
  };
  if (options.token) headers.Authorization = `Bearer ${options.token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${apiBase()}${path}`, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      signal: abort.signal,
    });
    const text = await res.text();
    let body: unknown = null;
    try {
      body = text ? (JSON.parse(text) as unknown) : null;
    } catch {
      body = null;
    }
    return { status: res.status, body };
  } catch {
    return { status: 'network' };
  } finally {
    clearTimeout(timer);
  }
}

/** Calls `cb` whenever the device gets its connection back. */
export function onConnectionBack(cb: () => void): void {
  window.addEventListener('online', cb);
}
